import { useAppContext } from 'context/app';
import React from 'react';
import { Spacer24 } from 'steps/styled';
import Text from '@service-victoria/component-library/components/Text';
import { AddAvailability } from 'interfaces/store';
import * as Styled from './styled';
import { constants } from 'utils';

type TooEarlyProps = {
  updateAvailability: AddAvailability;
};

function timeUntilMidday(startTime?: string) {
  const currentDateTime = new Date();
  const middayRelease = new Date(startTime || '');

  const diffInTime = middayRelease.getTime() - currentDateTime.getTime();
  const diffHours = Math.max(0, Math.floor(diffInTime / (1000 * 3600)));
  const diffMins = Math.ceil((diffInTime / (1000 * 60)) % 60);

  if (diffMins < 0) return `00hrs 00mins`;

  const prependZero = (number: number) => (number < 10 ? `0${number}` : `${number}`);

  return `${prependZero(diffHours)}hrs ${prependZero(diffMins)}min${diffMins !== 1 ? 's' : ''}`;
}

export default function TooEarly({ updateAvailability }: TooEarlyProps) {
  const { currentState } = useAppContext();
  const [timeToRender, setTimeToRender] = React.useState<string>('');

  React.useEffect(() => {
    setTimeToRender(timeUntilMidday(currentState.startTime));

    const timer = setInterval(() => {
      const timeToRender = timeUntilMidday(currentState.startTime);
      setTimeToRender(timeToRender);

      if (timeToRender === '00hrs 00mins') {
        updateAvailability('SUCCESS', currentState.startTime);
        clearInterval(timer);
      }
    }, 5000);
    return () => {
      clearInterval(timer);
    };
  }, [currentState.startTime, updateAvailability]);

  return (
    <>
      <Text.Heading.H2 style={{ fontWeight: 600 }}>You&apos;re early.</Text.Heading.H2>
      <Text.Body>
        Melbourne travel voucher applications open at 10am on {constants.contentDates.goLiveDay}{' '}
        {constants.contentDates.goLive}.​
      </Text.Body>
      <Text.Body as='div'>
        Please come back in{' '}
        <Text.Heading.H2 style={{ display: 'inline-block' }}>{timeToRender}</Text.Heading.H2>
      </Text.Body>
      <Text.Body>
        These travel vouchers apply to travel in the period of{' '}
        {constants.contentDates.travelPeriodStart} to {constants.contentDates.travelPeriodEnd}{' '}
        2021.​
      </Text.Body>
      <Text.Body>
        Want to be ready on the day? Make sure you have your Victorian driver licence, or seniors
        card or proof-of-age card handy. You&apos;ll need it to complete your application.
      </Text.Body>
      <Spacer24 />
      <Text.Heading.H3 style={{ fontWeight: 600 }}>What does the scheme offer?</Text.Heading.H3>
      <Text.Body>You can apply for a voucher to claim a reimbursement of $200.</Text.Body>
      <Styled.List>
        <Styled.ListItem>
          You&apos;ll need to spend at least $400 on a minimum two nights&apos; paid accommodation,
          plus tourism attractions, paid tours or experiences in metropolitan Melbourne.
        </Styled.ListItem>
        <Styled.ListItem>
          You must spend at least two nights in paid accommodation in any of the 26 local government
          areas in metropolitan Melbourne. This excludes areas already covered by the Regional
          Travel Voucher Scheme including the Yarra Valley, Dandenong Ranges, Frankston and the
          Mornington Peninsula. Click{' '}
          <Text.Link href={constants.externalLinks.eligibleLGAs} value='here' target='_blank' /> to
          view the list of eligible LGAs.
        </Styled.ListItem>
        <Styled.ListItem>One voucher per household for the whole scheme.</Styled.ListItem>
        <Styled.ListItem>You must be over 18 and a Victorian resident to apply.</Styled.ListItem>
        <Styled.ListItem>
          Vouchers are redeemable after the date of travel, with proof of purchase
        </Styled.ListItem>
      </Styled.List>
      <Spacer24 />
      <Text.Heading.H3 style={{ fontWeight: 600 }}>Travel vouchers do not cover:</Text.Heading.H3>
      <Spacer24 />
      <Styled.List>
        <Styled.ListItem>Food or drinks</Styled.ListItem>
        <Styled.ListItem>
          Alcohol (unless as part of a paid wine/brewery/distillery tasting tour)
        </Styled.ListItem>
        <Styled.ListItem>Fuel or other transport costs</Styled.ListItem>
        <Styled.ListItem>Groceries</Styled.ListItem>
        <Styled.ListItem>Personal items e.g. clothing </Styled.ListItem>
        <Styled.ListItem>Gaming</Styled.ListItem>
      </Styled.List>
      <Spacer24 />
      <Styled.TextBodyWithInlineLink>
        While you&apos;re waiting,{' '}
        <Text.Link
          value='read more about the scheme'
          target='_blank'
          href={constants.externalLinks.metroVoucherScheme}
        />{' '}
        and what you can claim.
      </Styled.TextBodyWithInlineLink>
    </>
  );
}
